

import { CoveringLoadingSpinner } from "@/components/LoadingSpinner/CoveringLoadingSpinner";
import { GuestLogType } from "@/types/enums/GuestLogType";
import { GuestLog } from "@/types/GuestLog";
import {
  IconArrowBackUp,
  IconDeviceFloppy,
  IconDeviceMobile,
  IconPencil,
  IconX,
} from "@tabler/icons-react";
import { useDocumentation } from "../hooks/useDocumentation";
import { useRole } from "@/providers/RoleProvider";

type DocumentationProps = {
  guest: GuestLog;
  isLast?: boolean;
};

export const Documentation: React.FC<DocumentationProps> = ({
  guest: entry,
  isLast = false,
}) => {
  const { legacyRoleFloor, role } = useRole();
  const {
    editMode,
    saveError,
    visibleNote,
    hasChanges,
    isLoading,
    text,
    getStatusColor,
    formatDate,
    formatTime,
    handleSave,
    handleTextChange,
    handleEnterHover,
    handleLeaveHover,
    handleEnableEditMode,
    handleCancelEdit,
    handleResetText,
    t,
  } = useDocumentation(entry);

  /**
   * Own NOTE rows are editable in place; merged COMPANION rows are not.
   *
   * `handleSave` posts against `entry.guestId`, so editing a companion's note
   * from here would technically hit the right row — but the host's timeline is
   * a reading surface, and a note that belongs to someone else does not get
   * edited from someone else's history. It is also what keeps step 5's
   * name-prefixed note text (`useDocumentation`) from ever travelling back into
   * `updateNote`.
   */
  const isCompanionRow = !!entry.companionId;
  const noteIsEditable =
    entry.type === GuestLogType.NOTE &&
    !isCompanionRow &&
    legacyRoleFloor !== "VIEWER";

  return (
    <div
      onMouseEnter={handleEnterHover}
      onMouseLeave={handleLeaveHover}
      className={`relative w-full pl-10 ${
        isLast ? "pb-0" : "pb-2"
      } pt-2 rounded-[10px] ${
        noteIsEditable && (editMode || visibleNote) ? "surface-secondary" : ""
      } `}
    >
      {/* Timeline line segment - startet vom Punkt und geht bis zum Ende des Containers */}
      {!isLast && (
        <div
          className="absolute left-3.75 w-0.5 surface-outline-quaternary"
          style={{
            top: "18px",
            bottom: "-8px",
          }}
        />
      )}

      {/* Timeline dot */}
      <div className="absolute left-2 top-1.5 w-4 h-4 rounded-full border-4 surface-font-tertiary outline-quaternary"></div>

      {noteIsEditable && visibleNote && !editMode ? (
        <IconPencil
          onClick={handleEnableEditMode}
          size={16}
          strokeWidth={1.5}
          className="absolute right-2 top-0"
        />
      ) : noteIsEditable && editMode ? (
        <div className="flex flex-row justify-end absolute left-0 right-0 top-0 gap-1 pt-1 pr-1">
          {hasChanges && (
            <>
              <button disabled={isLoading} onClick={handleResetText}>
                <IconArrowBackUp size={16} strokeWidth={1.5} />
              </button>
              <button disabled={isLoading} onClick={handleSave}>
                <IconDeviceFloppy size={16} strokeWidth={1.5} />
              </button>
            </>
          )}

          <button disabled={isLoading} onClick={handleCancelEdit}>
            <IconX size={16} strokeWidth={1.5} />
          </button>
        </div>
      ) : (
        <div> </div>
      )}
      {/* Entry content */}
      <div className="w-full">
        <div className="body-4-regular font-quaternary">
          {/* No type, no chip. `DocumentMapper` reads a `guest_logs.type` that
              main-backend's enum does not carry as NULL — deliberately, so one
              stale row cannot break a whole read — and until 2026-09-01 the
              seven types the satellites write but the enum lacked arrived here
              as null. The chip rendered anyway: a coloured pill with no text
              beside a bare timestamp, 1568 rows of it in production, and
              nothing in any log. The enum is fixed; this makes the NEXT one a
              row without a chip instead of a row with an empty one. */}
          <span
            className={`mr-1 px-2 py-0.5 h-max rounded-md ${getStatusColor(
              entry.type,
              entry.companionId,
            )}`}
            hidden={!entry.type}
          >
            {/* ALWAYS the row type. Swapping it for the word "Companion" on a
                merged row destroyed two shapes at once: the backend stamps
                `field: "checkedIn"` for BOTH check-in and check-out, so with
                the type gone from the chip a door entry and a door exit
                rendered identically — and a PROMOTED row, whose body is
                deliberately empty because "the chip carries the meaning",
                rendered as a chip, a timestamp and nothing else.
                Companion membership is carried by the pink chip colour
                (`getStatusColor` keys on companionId) and by the name in the
                sentence, neither of which costs the type. */}
            {t(entry.type)}
          </span>
          {formatDate(entry.createdAt)} ({formatTime(entry.createdAt)})
          {entry?.firstName &&
            entry.lastName &&
            ` @${entry?.firstName} ${entry.lastName}`}
          {entry.importName && ` · ${t("viaImport")}: "${entry.importName}"`}
          {/* FW12 — an automation has no user row, so the `@Name` above stays
              blank and the change would otherwise read like a guest's own
              email-link RSVP. Same trailing-marker shape as the import note. */}
          {entry.actorType === "AUTOMATION" && ` · ${t("viaAutomation")}`}
          {/* Check-in app: the device stamps its own id into `userId`, so the
              `@Name` above is blank too — same failure mode as the automation
              marker. Named + icon-marked so a door scan is distinguishable
              from a cockpit click at a glance. */}
          {entry.deviceName && (
            <span className="inline-flex flex-wrap items-center gap-1 align-middle ml-1 max-w-full">
              ·
              {/* 11px, not 13: this line is `body-4-regular` = 0.625rem = 10px,
                  and an oversized icon grows the line box on this row alone.
                  `flex-wrap` + `min-w-0` so a long door name wraps instead of
                  running out of the guest-detail rail. */}
              <IconDeviceMobile size={11} strokeWidth={1.5} className="shrink-0" />
              <span className="min-w-0 break-words">
                {`${t("viaDevice")}: "${entry.deviceName}"`}
              </span>
            </span>
          )}
        </div>

        <div className="mt-1 w-full body-4-regular">
          {!noteIsEditable ? (
            <p className="w-full">{text}</p>
          ) : (
            <textarea
              onChange={handleTextChange}
              value={text}
              readOnly={editMode ? false : true}
              disabled={isLoading}
              className={`
            outline-hidden w-full
            ${
              editMode
                ? "surface-secondary p-1 rounded-lg border outline-quaternary"
                : visibleNote
                  ? "surface-secondary"
                  : "surface-primary"
            } `}
            />
          )}
          {isLoading && <CoveringLoadingSpinner />}
          {/* A rejected save has to be visible where the user is looking. The
              provider used to swallow it into a global field that only the
              timeline rendered — so the editor closed and the change looked
              applied until the next reload. */}
          {saveError && (
            <p className="font-error body-4-regular mt-1">{saveError}</p>
          )}
        </div>
      </div>
    </div>
  );
};
